import React from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Stack,
    Divider,
    Tooltip,
    useTheme,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import { useTranslation } from 'react-i18next';

import { useSettingsContext } from '../../../setting/SettingsContext';
import ModeOptions from './sections/ModeOptions';
import PresetsOptions from './sections/PresetsOptions';
import FontOptions from './sections/FontOptions';
import StretchOptions from './sections/StretchOptions';
import NavColorOptions from './sections/NavColorOptions';
import LayoutOptions from './sections/LayoutOptions';

// NOTE: Direction (LTR/RTL) is intentionally NOT exposed here.
// Direction is already driven by the navbar's language toggle
// (TransLang → onChangeDirectionByLang), and surfacing it as a
// second control in the drawer creates a confusing two-source-of-truth
// problem when the user picks a direction that contradicts their
// chosen language.

const DRAWER_WIDTH = 320;

/**
 * Side drawer that aggregates the admin's global UI preferences.
 *
 * Sections shipped in Sprint S-2 (this file):
 *   - Mode (light / dark)
 *   - Direction (ltr / rtl)
 *   - Reset
 *
 * Color presets and font family slot in via additional <Section /> imports
 * in the next sprints. The drawer's structure is intentionally
 * composable so each sprint adds one section without restructuring this
 * file.
 */
const SettingsDrawer = ({ open, onClose }) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const { onResetSetting } = useSettingsContext();

    return (
        <Drawer
            anchor={isArabic ? 'left' : 'right'}
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: DRAWER_WIDTH,
                    bgcolor: theme.palette.background.default,
                    backgroundImage: 'none',
                    direction: isArabic ? 'rtl' : 'ltr',
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Typography variant="subtitle1" fontWeight={700}>
                    {t('Settings', { defaultValue: isArabic ? 'الإعدادات' : 'Settings' })}
                </Typography>
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title={isArabic ? 'استعادة الافتراضيات' : 'Reset all'} arrow>
                        <IconButton
                            size="small"
                            onClick={onResetSetting}
                            aria-label="Reset settings"
                        >
                            <RestartAltRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={onClose} aria-label="Close settings">
                        <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Box>

            {/* Body */}
            <Box sx={{ px: 2, py: 2, overflowY: 'auto' }}>
                <Stack spacing={3} divider={<Divider flexItem />}>
                    <ModeOptions />
                    <LayoutOptions />
                    <NavColorOptions />
                    <StretchOptions />
                    <PresetsOptions />
                    <FontOptions />
                </Stack>
            </Box>
        </Drawer>
    );
};

export default SettingsDrawer;
